import { makeAutoObservable } from 'mobx';
import {
  clearPersistedStore,
  getPersistedStore,
  hydrateStore,
  isHydrated,
  makePersistable,
} from 'mobx-persist-store';
import { parse } from 'date-fns/parse.js';
import { subDays } from 'date-fns/subDays.js';

import { ApiCallResult, Notification } from '../models/notification.js';
import {
  NotificationFilters,
  NotificationDateFilters,
} from '../constants/notification-types.js';
import {
  NotificationGroups,
  NotificationStatus,
  DefaultDateFormat,
  DefaultDisplayDays,
  ApiCallState,
} from '../constants/notifications.js';
import { ApiClient } from '../services/api-client.js';
import {
  mapNotificationDataToNotification,
  mapNotificationResponseToNotifications,
  mapParametersToPatchNotificationRequest,
  mapParametersToNotificationRequest,
  mapParametersToDeleteNotificationRequest,
  getNextPageCursorfromQueryParam,
} from '../services/notifications-service.js';
import { handleError } from '../helper/errors.js';
import { getUserAuthorization } from '../services/app-service.js';
import { NotificationBulkPatchResult } from '../api/notifications-api/index.js';

class NotificationState {
  constructor() {
    makeAutoObservable(this);
    makePersistable(this, {
      name: 'notification-state',
      properties: ['notificationFilters', 'nextPageCursor'],
      storage: window.localStorage,
    });
  }

  get isHydrated() {
    return isHydrated(this);
  }

  async hydrateStore() {
    await hydrateStore(this);
  }

  async clearStoredData() {
    await clearPersistedStore(this);
  }

  async getStoredData() {
    return getPersistedStore(this);
  }

  showDrawer: boolean = false;

  apiClient: ApiClient = { noInit: true } as ApiClient;

  baseApiUrl: string = '';

  localStorageKey: string = '';

  public NewApiClient = () => {
    this.apiClient = new ApiClient({
      NotificationApiUrl: this.baseApiUrl,
      getAuthorizationFn: () => getUserAuthorization(this.localStorageKey),
    });

    this.getNotifications();
  };

  public ConstructApiClient() {
    return this.apiClient.noInit ? this.NewApiClient() : this.apiClient;
  }

  nextPageCursor: string | undefined = '';

  submissionResult?: any;

  notifications: Notification[] = [] as Notification[];

  unreadNotificationCount: number = 0;

  notificationFilters: NotificationFilters = {
    SHOW_PLANNED_MAINTENANCE: true,
    SHOW_SERVICE_TICKETS: true,
    SHOW_DCIM_ALERTS: true,
  } as NotificationFilters;

  selectedNotification: Notification = {
    id: '',
    resourceType: NotificationGroups.PLANNED_MAINTENANCE,
    subGroup: '',
    subject: '',
    resourceId: '',
    locations: [],
    createdAt: '',
    accountNumber: '',
    createdBy: '',
    status: NotificationStatus.READ,
  } as Notification;

  dateFilters: NotificationDateFilters = {
    FROM_DATE: subDays(new Date(), DefaultDisplayDays),
    TO_DATE: new Date(),
  } as NotificationDateFilters;

  async clearStoredNotificationsData() {
    this.notifications = [] as Notification[];
    this.unreadNotificationCount = 0;
    this.nextPageCursor = '';
    this.apiClient = { noInit: true } as ApiClient;
  }

  public async setNotificationReadStatus(Id: String): Promise<Boolean> {
    const result = await this.setNotificationAsRead(Id.toString());
    if ((await result) === true) {
      this.ReduceUnreadNotificationCount();
      const itemIndex = this.notifications.findIndex(item => item.id === Id);
      this.notifications[itemIndex].status = NotificationStatus.READ;
      this.selectedNotification = this.notifications[itemIndex];
    }
    return result;
  }

  public async setNotificationDeleted(Id: String): Promise<ApiCallResult> {
    const result = await this.DeleteNotification(Id.toString());
    if (result === true) {
      const deleteditem = this.notifications.find(item => item.id === Id);
      if (deleteditem?.status === 'unread')
        this.ReduceUnreadNotificationCount();

      const itemIndex = this.notifications.findIndex(item => item.id === Id);
      this.notifications.splice(itemIndex, 1);
    }

    return {
      State: result === true ? ApiCallState.SUCCESS : ApiCallState.ERROR,
      Message:
        result === true
          ? 'Successfully deleted the notification'
          : 'Error while deleting the notification',
    } as ApiCallResult;
  }

  public setNotificationDateFilter(fromDate?: string, toDate?: string) {
    if (fromDate !== undefined) {
      this.dateFilters.FROM_DATE = parse(
        fromDate,
        DefaultDateFormat,
        new Date()
      );
    } else {
      this.dateFilters.FROM_DATE = undefined;
    }

    if (toDate !== undefined) {
      this.dateFilters.TO_DATE = parse(toDate, DefaultDateFormat, new Date());
    } else {
      this.dateFilters.TO_DATE = undefined;
    }
  }

  public setNotificationFilter(Id: String | undefined, value: Boolean) {
    switch (Id) {
      case NotificationGroups.PLANNED_MAINTENANCE:
        this.notificationFilters.SHOW_PLANNED_MAINTENANCE = value;
        break;
      case NotificationGroups.SERVICE_TICKETS:
        this.notificationFilters.SHOW_SERVICE_TICKETS = value;
        break;
      case NotificationGroups.DCIM_ALERTS:
        this.notificationFilters.SHOW_DCIM_ALERTS = value;
        break;
      default:
    }
  }

  public async getNotifications() {
    let result;
    // this.nextPageCursor === undefined means Next page not available
    if (this.nextPageCursor !== undefined) {
      result = await this.apiClient?.notificationsApi
        .getNotificationsRaw(
          mapParametersToNotificationRequest(
            getNextPageCursorfromQueryParam(this.nextPageCursor)
          )
        )
        .catch((e: any) => {
          handleError(e, false);
          return undefined;
        });

      if (result) {
        const responseResult = await result?.value();
        this.notifications =
          this.nextPageCursor === ''
            ? mapNotificationResponseToNotifications(responseResult?.items)
            : this.notifications.concat(
                mapNotificationResponseToNotifications(responseResult?.items)
              );
        this.nextPageCursor =
          responseResult?.next !== undefined ? responseResult?.next : undefined;

        this.unreadNotificationCount =
          result.raw.headers.get('x-total-unread-count') !== null &&
          result.raw.headers.get('x-total-unread-count') !== undefined
            ? (result.raw.headers.get(
                'x-total-unread-count'
              ) as unknown as number)
            : 0; // eslint-disable-line
      }
    }
  }

  public async getNotificationsById(id?: string) {
    let result;
    if (id) {
      result = await this.apiClient?.notificationsApi
        .getNotificationsById({ id })
        .catch((e: any) => {
          handleError(e, false);
          return undefined;
        });
    }
    if (result) {
      this.selectedNotification = mapNotificationDataToNotification(result);
    }
  }

  private async setNotificationAsRead(id?: string): Promise<Boolean> {
    if (id) {
      await this.apiClient?.notificationsApi
        .patchNotificationsById(
          mapParametersToPatchNotificationRequest(id, 'read')
        )
        .catch((e: any) => {
          handleError(e, false);
          return false;
        });
      return true;
    }
    return false;
  }

  async setAllNotificationAsRead(): Promise<
    NotificationBulkPatchResult | boolean
  > {
    const result = await this.apiClient?.notificationsApi
      .patchNotifications({
        notificationBulkPatch: { status: NotificationStatus.READ },
      })
      .catch((e: any) => {
        handleError(e, false);
        return false;
      });

    this.notifications = this.notifications.map(notification => ({
      ...notification,
      status: NotificationStatus.READ,
    }));

    this.unreadNotificationCount = 0;

    return result;
  }

  private async DeleteNotification(id?: string): Promise<boolean> {
    let result = false;
    if (id) {
      await this.apiClient?.notificationsApi
        .deleteNotificationsById(mapParametersToDeleteNotificationRequest(id))
        .catch((e: any) => {
          handleError(e, false);
          result = false;
        })
        .then(() => {
          result = true;
        });
    }
    return result;
  }

  private ReduceUnreadNotificationCount() {
    if (this.unreadNotificationCount > 0) this.unreadNotificationCount -= 1;
  }
}

const NotificationsState = new NotificationState();

export { NotificationsState };
